import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Title, Text, TextInput, Button, Box, Group, Stack } from '@mantine/core';
import { apiClient } from '../services/api';
import { Game, Player } from '../types/models';
import SVGDrawingBoard, { SVGDrawingBoardHandle } from './SVGDrawingBoard';
import { DrawingControlsMantine } from './DrawingControlsMantine';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { useSquareCanvas } from '../hooks/useSquareCanvas';
import { GameLayout } from './layouts/GameLayout';
import { useDrawingPersistence } from '../hooks/useDrawingPersistence';
import { AVATAR_FULL_SIZE, AVATAR_THUMB_SIZE, createAvatarThumbnailFromBlob } from '../utils/avatarImages';
import { DrawingData, DrawingTool } from '../utils/drawingExport';

interface JoinGameProps {
  game: Game;
  onPlayerCreated: (player: Player) => void;
}

const JoinGame: React.FC<JoinGameProps> = ({ game, onPlayerCreated }) => {
  const { isMobile } = useResponsiveLayout();
  const { width: canvasSize } = useSquareCanvas();
  const drawingBoardRef = useRef<SVGDrawingBoardHandle | null>(null);
  
  const [name, setName] = useState(() => {
    return localStorage.getItem('userName') || '';
  });
  const [avatarUrl, setAvatarUrl] = useState<string | null>(() => {
    return localStorage.getItem('userAvatarUrl');
  });
  const [avatarThumbUrl, setAvatarThumbUrl] = useState<string | null>(() => {
    return localStorage.getItem('userAvatarThumbUrl');
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentTool, setCurrentTool] = useState<DrawingTool>('pen');
  const [currentSize, setCurrentSize] = useState(5);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [canClear, setCanClear] = useState(false);
  const [strokeCount, setStrokeCount] = useState(0);
  const [hasDrawnAvatar, setHasDrawnAvatar] = useState(false);
  const [pendingUploadPromise, setPendingUploadPromise] = useState<Promise<string> | null>(null);
  const uploadPromiseRef = useRef<Promise<string> | null>(null);
  const { loadDrawing, saveDrawing, clearDrawing } = useDrawingPersistence({
    gameKey: game.key,
    stackId: 'avatar',
    entryNumber: 0
  });

  useEffect(() => {
    let isActive = true;
    const restoreDrawing = async () => {
      const saved = await loadDrawing();
      if (!isActive || !saved) return;
      drawingBoardRef.current?.loadDrawingData(saved);
    };

    restoreDrawing();

    return () => {
      isActive = false;
    };
  }, [loadDrawing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if avatar has enough strokes (we trust the DrawingBoard's validation here)
    if (strokeCount < 1) {
      setError("C'mon, that's not a complete avatar. Add a little more!");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Wait for pending upload if there is one
      let finalAvatarUrl = avatarUrl;
      
      // Always force a final save if user has drawn something, even if we have a URL
      // This ensures we capture the very latest drawing state
      if (strokeCount > 0) {
        setError(null);
        
        // Clear any pending upload promise tracking
        setPendingUploadPromise(null);
        
        // Create a promise that resolves when avatar upload completes
        const finalSavePromise = new Promise<string>((resolve, reject) => {
          let uploadResolved = false;
          
          // Set up a one-time listener for the avatar URL update
          const checkForNewAvatar = (newUrl: string) => {
            if (!uploadResolved) {
              uploadResolved = true;
              resolve(newUrl);
            }
          };
          
          // If we already have a URL, still wait for the new upload to complete
          // This handles the case where auto-save captured an incomplete drawing
          const startTime = Date.now();
          const pollForUpdate = () => {
            // Check if we got a new upload
            if (uploadPromiseRef.current) {
              uploadPromiseRef.current
                .then(checkForNewAvatar)
                .catch(reject);
            } else if (Date.now() - startTime > 5000) {
              // Timeout after 5 seconds
              reject(new Error('Avatar save timeout'));
            } else {
              // Keep polling
              setTimeout(pollForUpdate, 50);
            }
          };
          
          // Trigger the save immediately
          console.log('Triggering final avatar save...');
          drawingBoardRef.current?.handleSave();
          
          // Start polling for the result
          setTimeout(pollForUpdate, 10);
        });
        
        try {
          finalAvatarUrl = await finalSavePromise;
          console.log('Final avatar URL:', finalAvatarUrl);
        } catch (err) {
          console.error('Avatar save failed:', err);
          // If we have an existing URL, use it as fallback
          if (avatarUrl) {
            console.log('Using existing avatar URL as fallback:', avatarUrl);
            finalAvatarUrl = avatarUrl;
          } else {
            setError('Failed to upload avatar. Please try again.');
            setIsLoading(false);
            return;
          }
        }
      }
      
      // Save user preferences to localStorage
      localStorage.setItem('userName', name);
      
      // Set a random handwriting font if not already set
      const fonts = [
        'Kalam', 'Caveat', 'Architects Daughter', 'Patrick Hand', 'Indie Flower',
        'Permanent Marker', 'Shadows Into Light', 'Gochi Hand', 'Reenie Beanie',
        'Gloria Hallelujah', 'Amatic SC', 'Rock Salt', 'Nothing You Could Do',
        'Covered By Your Grace', 'Just Another Hand', 'La Belle Aurore',
        'Cedarville Cursive', 'Waiting for the Sunrise'
      ];
      let handwritingFont = localStorage.getItem('userHandwriting');
      if (!handwritingFont) {
        handwritingFont = fonts[Math.floor(Math.random() * fonts.length)];
        localStorage.setItem('userHandwriting', handwritingFont);
      }
      
      const storedThumbUrl = localStorage.getItem('userAvatarThumbUrl');
      const finalAvatarThumbUrl = storedThumbUrl || avatarThumbUrl || undefined;

      const { data: player } = await apiClient.createPlayer(game.key, {
        name,
        avatarUrl: finalAvatarUrl,
        avatarThumbUrl: finalAvatarThumbUrl,
        isHost: false,
        handwritingFont: handwritingFont
      });

      localStorage.setItem(`game_${game.key}_player_id`, player.id.toString());

      onPlayerCreated(player);

    } catch (err: any) {
      // Check for specific error types
      if (err.error_code === 'NAME_TAKEN' || err.errors?.[0]?.includes('already taken')) {
        setError("That name is already taken. Please choose a different name.");
      } else if (err.errors && Array.isArray(err.errors)) {
        // Handle array of errors from Rails
        setError(err.errors.join(', '));
      } else {
        setError(err.error || 'Failed to join game. Please try again.');
      }
      console.error('Game join error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarSubmit = useCallback(async (blob: Blob) => {
    console.log('JoinGame: handleAvatarSubmit called with blob:', {
      type: blob.type,
      size: blob.size,
      hasBlob: !!blob
    });

    // Create a promise that we can track
    const uploadPromise = (async () => {
      const thumbBlob = await createAvatarThumbnailFromBlob(blob, AVATAR_THUMB_SIZE);
      const [fullResponse, thumbResponse] = await Promise.all([
        apiClient.uploadDrawing(blob),
        apiClient.uploadDrawing(thumbBlob)
      ]);

      console.log('Avatar uploaded successfully:', {
        url: fullResponse.data.url,
        response: fullResponse.data
      });

      localStorage.setItem('userAvatarUrl', fullResponse.data.url);
      localStorage.setItem('userAvatarThumbUrl', thumbResponse.data.url);
      requestAnimationFrame(() => {
        setAvatarUrl(fullResponse.data.url);
        setAvatarThumbUrl(thumbResponse.data.url);
        setPendingUploadPromise(null);
        if (error) {
          setError(null);
        }
      });

      return fullResponse.data.url;
    })()
      .catch((err) => {
        console.error('Failed to upload avatar:', err);
        // Only update error state after a delay to avoid interrupting drawing
        setTimeout(() => {
          setError('Failed to upload avatar. Please try again.');
          setPendingUploadPromise(null);
        }, 100);
        throw err;
      });
    
    // Track the promise
    setPendingUploadPromise(uploadPromise);
    
    // Store the promise so we can wait for it later
    uploadPromiseRef.current = uploadPromise;
    uploadPromise.catch(() => {
      // Error handled in the uploadPromise catch above
    });
  }, [error]);

  const handleAvatarPersist = useCallback((drawingData: DrawingData | null) => {
    if (drawingData) {
      saveDrawing(drawingData);
      return;
    }
    clearDrawing();
    localStorage.removeItem('userAvatarUrl');
    localStorage.removeItem('userAvatarThumbUrl');
  }, [clearDrawing, saveDrawing]);

  const handleClear = () => {
    // Clear the drawing board
    drawingBoardRef.current?.clear();
    setAvatarUrl(null);
    setAvatarThumbUrl(null);
    setCanClear(false);
    setCanUndo(false);
    setCanRedo(false);
    setError(null);
    setStrokeCount(0);
    setHasDrawnAvatar(false);
    localStorage.removeItem('userAvatarUrl');
    localStorage.removeItem('userAvatarThumbUrl');
  };

  const hostName = game.players.find(p => p.isHost)?.name || 'Someone';

  // Header buttons
  const clearButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClear}
      style={{ minWidth: '70px' }}
    >
      Clear
    </Button>
  );
  
  const joinButton = (
    <Button
      size="sm"
      disabled={isLoading || !name || !hasDrawnAvatar}
      onClick={handleSubmit}
      loading={isLoading}
      style={{ minWidth: '70px' }}
    >
      Join
    </Button>
  );

  // Header content with inputs
  const header = (
    <Stack gap="xs">
      <Group justify="space-between" align="center" wrap="nowrap">
        {clearButton || <Box style={{ width: '60px' }} />}
        <Title 
          order={isMobile ? 3 : 2} 
          ta="center" 
          style={{ 
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          Join {hostName}'s game
        </Title>
        {joinButton || <Box style={{ width: '60px' }} />}
      </Group>
      <Group gap="sm" grow>
        <TextInput
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          size={isMobile ? "md" : "lg"}
        />
      </Group>
      {error && (
        <Text c="red" size="sm" ta="center" mt="xs">
          {error}
        </Text>
      )}
    </Stack>
  );

  // Drawing content
  const drawingContent = (
    <Box style={{ position: 'relative', width: canvasSize, height: canvasSize }}>
      <SVGDrawingBoard
        ref={drawingBoardRef}
        width={AVATAR_FULL_SIZE}
        height={AVATAR_FULL_SIZE}
        onSave={handleAvatarSubmit}
        onPersist={handleAvatarPersist}
        currentColor={currentColor}
        currentTool={currentTool}
        strokeWidth={currentSize}
        onCanUndoChange={setCanUndo}
        onCanRedoChange={setCanRedo}
        onStrokeCountChange={(count) => {
          setStrokeCount(count);
          setHasDrawnAvatar(count > 0);
          setCanClear(count > 0);
        }}
      />
    </Box>
  );

  // Bottom controls - drawing controls
  const bottomControls = (
    <DrawingControlsMantine
      currentColor={currentColor}
      currentTool={currentTool}
      currentSize={currentSize}
      onColorChange={setCurrentColor}
      onSizeChange={setCurrentSize}
      onToolChange={setCurrentTool}
      onUndo={() => drawingBoardRef.current?.handleUndo()}
      onRedo={() => drawingBoardRef.current?.handleRedo()}
      canUndo={canUndo}
      canRedo={canRedo}
      onClear={handleClear}
      canClear={canClear}
      hideClear={true}
      showEraser
    />
  );

  // Use GameLayout for all screen sizes
  return (
    <GameLayout
      header={header}
      content={drawingContent}
      bottomControls={bottomControls}
      mode="draw"
    />
  );
};

export default JoinGame; 
